import {
  AfterContentInit,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { XUXListContainerComponent } from '@app/containers/base/list/xux-list.container.component';
import { WIDGETS_STATIC_TEXT_CONTENT } from '@app/i18n/constants/static-text-content.constants';
import { I18n } from '@app/i18n/strings-keys';
import { rx } from '@app/shared/rx';
import {
  ENTITY_TYPES,
  ICON_TYPE,
  IDocumentOperationOptions,
  IFieldPreferences,
  IFilterQuery,
  IGridOptions,
  IRequestPayload,
  XuxGridOptions,
} from '@app/shared/types';
import { STAND_WORKSHEET_STATUS_VALUE } from '@app/shared/types/enums/stand-worksheet-status.enum';
import {
  getIcon,
  getObservableValue,
  isNotClickGridCell,
} from '@app/shared/utils';
import { IUIListState } from '@app/store/reducers/ui';
import {
  ENTITY_ID_PATH,
  INTERVAL_FETCH_TIME,
  XUX_FORM_THEME_CLASS,
} from '@config/global-constants';
import {
  get,
  has,
  includes,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  omit,
  replace,
  union,
} from 'lodash';
import { BehaviorSubject } from 'rxjs';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xux-entity-form-list',
  templateUrl: 'xux-entity-form-list.component.html',
  styleUrls: [
    '../../../containers/base/list/xux-list.container.scss',
    'xux-entity-form-list.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
/**
 * This component currently only supports sales orders for stand worksheets.
 */
export class XUXEntityFormListComponent
  extends XUXListContainerComponent
  implements OnInit, AfterContentInit, OnDestroy
{
  @Input() public field: IFieldPreferences;
  public iconPath: string = getIcon(ICON_TYPE.DOUBLE_RIGHT);
  public isCollapsed: boolean = true;
  public entity: ENTITY_TYPES;
  public disableLoader: boolean = false;
  public composedPayload: IRequestPayload;
  public parentViewSetting: IUIListState;
  public isInitialized: boolean = true;
  public gridOptions: XuxGridOptions = {
    orderControls: false,
    multiselect: false,
    animateRows: false,
    showViewSelector: false,
    searchPlaceholder: this.i18nService.instant(I18n.Common.Search),
    showPagination: true,
    valueField: ENTITY_ID_PATH,
    disableColumnSelector: true,
    gridHeight: '280px',
    availablePageSizes: [5],
  };
  public staticI18nTextContent = this.i18nService.translateStaticTextContent({
    xuxGrid: WIDGETS_STATIC_TEXT_CONTENT.xuxGrid,
  });

  private intervalFetchId: NodeJS.Timeout = null;

  @HostListener('document:click', ['$event'])
  public removeCellFocusClass(event: MouseEvent): void {
    if (isNotClickGridCell(event)) {
      this.gridApi?.clearFocusedCell();
    }
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.uiActions.setUiTheme(XUX_FORM_THEME_CLASS);
    this.subscriptions.push(
      this.viewConfig$
        .pipe(
          rx.first(),
          rx.filter(
            (viewConfig) =>
              viewConfig.entityType === ENTITY_TYPES.STAND_WORKSHEET,
          ),
          /** passing only the first argument "viewConfig" */
          rx.tap((viewConfig: IUIListState) => {
            this.parentViewSetting = viewConfig;
          }),
        )
        .subscribe(() => {}),
    );
  }

  public ngAfterContentInit(): void {
    this.headerName = this.field.title;
    const widgetData = this.field.widgetData;
    this.entity = get(widgetData, 'entity', null);
    this.gridOptions.overlayNoRowsTemplate = get(
      widgetData,
      'userGridOptions.overlayNoRowsTemplate',
      ' ',
    );
    this.isHiddenRefreshButton = true;
    this.entityService = this.entityServiceProvider.getService(this.entity);
    this.pageCount$ = this.stateService.select([
      'entities',
      ENTITY_TYPES.SALE_DEPLETION_ORDER,
      'page_count',
    ]);
    this.items$ = this.stateService.select([
      'entities',
      ENTITY_TYPES.SALE_DEPLETION_ORDER,
      'items',
    ]);
    this.total$ = this.stateService.select([
      'entities',
      ENTITY_TYPES.SALE_DEPLETION_ORDER,
      'total',
    ]);
    this.uiActions.restoreDefaultListView(this.entityType);
  }

  public ngOnDestroy(): void {
    clearTimeout(this.intervalFetchId);
    this.uiActions.updateDefaultListViewData(this.parentViewSetting);
    this.apiActions.storeClearItems(ENTITY_TYPES.SALE_DEPLETION_ORDER);
    super.ngOnDestroy();
  }

  /**
   * Toggles the expanded collapse state
   */
  public onSwitchIcon(): void {
    this.isCollapsed = !this.isCollapsed;
    this.iconPath = getIcon(
      this.isCollapsed ? ICON_TYPE.DOUBLE_RIGHT : ICON_TYPE.DOUBLE_LEFT,
    );
    if (this.isCollapsed) {
      clearTimeout(this.intervalFetchId);
    } else {
      this.fetchList(
        this.composeFilterQuery(getObservableValue(this.viewConfig$)),
        { noLoader: true },
        { gridLoader: true },
      );
      this.intervalFetchList(INTERVAL_FETCH_TIME);
    }
  }

  public async fetchList(
    query: IFilterQuery = {},
    options: IDocumentOperationOptions = { noLoader: true },
    gridOptions: IGridOptions = { gridLoader: false },
  ): Promise<any> {
    // in order to fetch current not waite 10s
    if (
      !isNil(options.continueFetchCondition) &&
      !options.continueFetchCondition(query)
    ) {
      return Promise.resolve({});
    }
    if (this.isInitialized) {
      this.isInitialized = false;

      return Promise.resolve({});
    }
    const widgetData = this.field.widgetData;
    const defaultFilter = isFunction(widgetData.filter)
      ? widgetData.filter()
      : undefined;
    // Document request should have similar bulk options
    const extendedQuery: IFilterQuery = {
      ...query,
      filter: has(query, 'filter')
        ? `${get(query, 'filter')} and ${defaultFilter}`
        : defaultFilter,
      top: 5,
      skip: (get(this.viewConfig, 'currentPage', 1) - 1) * 5,
    };
    this.composedPayload = this.privateComposeMasterQuery(
      extendedQuery,
      options,
    );
    if (gridOptions.gridLoader) {
      this.disableLoader = true;
    }
    const result = await this.apiActions.fetchList(
      this.entity,
      this.composedPayload.query,
      {
        ...this.composedPayload.options,
        keepUntilFetch: true,
      },
    );
    this.disableLoader = false;

    return result;
  }

  public initListTitle(): void {
    this.title$ = get(
      this.entityService,
      'config.list.titleObs',
      new BehaviorSubject(null),
    );
    this.uiActions.setUiTheme(XUX_FORM_THEME_CLASS);

    this.subscriptions.push(
      this.title$
        .pipe(
          rx.distinctUntilChanged(),
          rx.filter((title: string) => !!title),
        )
        .subscribe((title: string): void => {
          this.headerName = title;
        }),
    );
  }

  public setColumnDefinitions(): void {
    const widgetData = this.field.widgetData;
    this.columnDefinitions = get(widgetData, 'columns', []);
  }

  public intervalFetchList(time: number = 0): void {
    if (this.isCollapsed) return;
    const widgetData = this.field.widgetData;
    const defaultStatus = isFunction(widgetData.status)
      ? widgetData.status()
      : undefined;
    if (
      !defaultStatus ||
      !includes(
        [
          STAND_WORKSHEET_STATUS_VALUE.Open,
          STAND_WORKSHEET_STATUS_VALUE.Approved,
        ],
        defaultStatus,
      )
    ) {
      return;
    }
    clearTimeout(this.intervalFetchId);
    this.intervalFetchId = setTimeout(() => {
      const { query, options } = this.composedPayload;

      return this.apiActions
        .fetchList(this.entity, query, {
          ...options,
          keepUntilFetch: true,
          suppressErrors: true,
        })
        .finally(() => {
          this.intervalFetchList(INTERVAL_FETCH_TIME);
        });
    }, time);
  }

  private privateComposeMasterQuery(
    query: IFilterQuery,
    options: IDocumentOperationOptions,
  ): IRequestPayload {
    if (this.inShowDeletedMode) {
      return { query, options };
    }
    const notNeedActive = get(query, 'notNeedActive', false);
    const activeFilter: string = `${notNeedActive ? '' : 'and (is_active eq in(true))'}`;
    const extendedFilterWithActive: string =
      has(query, 'filter') && !isEmpty(query.filter)
        ? `${replace(query.filter, '(is_active eq in(true)) and ', '')}`
        : activeFilter;

    return omit({
      options,
      query: {
        ...query,
        select: union(query.select, ['transaction_date', 'is_post_closure']),
        filter: extendedFilterWithActive,
        orderby:
          has(query, 'orderby') &&
          !isEmpty(get(query, 'orderby')) &&
          !isEqual(get(query, 'orderby'), 'site_name asc')
            ? get(query, 'orderby')
            : 'created_at desc',
      },
    });
  }
}
